import store from '@/store'
import { ActionTypes } from '@/store/action-types'

/**
 * WebSocket Service for real-time notifications
 * Connects to backend WebSocket endpoint and listens for permission/action changes
 */
class WebSocketService {
  private ws: WebSocket | null = null
  private reconnectInterval = 5000
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private intentionallyClosed = false
  private pingTimer: ReturnType<typeof setInterval> | null = null

  /**
   * Connect to WebSocket server
   * @param url WebSocket server URL
   * @param token JWT authentication token
   */
  connect(url: string, token: string) {
    // Close existing connection if any
    if (this.ws) {
      this.intentionallyClosed = true
      this.ws.close()
    }

    this.intentionallyClosed = false

    try {
      const wsUrl = `${url}?token=${encodeURIComponent(token)}`
      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        this.startPing()
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMessage(data)
        } catch (e) {
          console.error('Error parsing WebSocket message:', e)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason)
        this.stopPing()

        // Attempt reconnection if not intentionally closed
        if (!this.intentionallyClosed) {
          console.log(`Reconnecting in ${this.reconnectInterval / 1000}s...`)
          this.scheduleReconnect(url, token)
        }
      }
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      this.scheduleReconnect(url, token)
    }
  }

  /**
   * Route incoming message to appropriate handler
   *
   * To add a new message type:
   * 1. Add case in switch statement
   * 2. Create handler method below
   *
   * Example:
   * case 'ASYNC_JOB_COMPLETED':
   *   this.handleAsyncJobCompleted(data)
   *   break
   */
  private handleMessage(data: any) {
    switch (data.type) {
      case 'CONNECTED':
        break

      case 'ACCOUNT_UPDATED':
        this.handleAccountUpdated(data)
        break

      case 'PONG':
        // Pong response to keep-alive ping
        break

      default:
        console.warn('⚠️ Unknown message type:', data.type)
    }
  }

  // ============================================================================
  // MESSAGE HANDLERS
  // ============================================================================

  /**
   * Handle account updated notification
   * Reloads user account data (permissions, actions, managers, etc.)
   */
  private async handleAccountUpdated(data: any) {
    try {
      await store.dispatch(ActionTypes.USER_REQUEST)

      // Show toast notification to user
      const vm: any = store.state.vm
      if (vm) {
        store.dispatch(ActionTypes.SHOW_TOAST, {
          message: vm.$t('websocket_notifications.account_updated.message'),
          title: vm.$t('websocket_notifications.account_updated.title'),
          variant: 'info'
        })
      }
    } catch (error) {
      console.error('Failed to reload user account data:', error)
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(url: string, token: string) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    this.reconnectTimer = setTimeout(() => {
      this.connect(url, token)
    }, this.reconnectInterval)
  }

  /**
   * Start sending ping messages to keep connection alive
   */
  private startPing() {
    this.stopPing()

    this.pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const ping = JSON.stringify({ type: 'PING', timestamp: Date.now() })
        this.ws.send(ping)
      }
    }, 25000)
  }

  /**
   * Stop sending ping messages
   */
  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.intentionallyClosed = true

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.stopPing()

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }
}

// Export singleton instance
export default new WebSocketService()
